import { Box, Button, Flex, Select, InputGroup, InputLeftElement, Input, Text, TableContainer, Table, Thead, Tbody, Th, Tr, Td, Checkbox, FormControl, FormLabel, InputRightElement, Image, Stack, Radio, HStack, Heading,
  Grid,
  GridItem,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Icon,
  useColorModeValue,
  Container,
  VStack } from "@chakra-ui/react";
import { TopDisplay } from "../../../components";
import { IoMdAdd as AddIcon } from "react-icons/io";
import { LuSearch as SearchIcon } from "react-icons/lu";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from 'react-query';
import styled from "@emotion/styled";
import { atom, useAtom } from 'jotai';
import apiCall from '../../../utils/apiCall';
import { openAtom, modalAtom, openNAtom, notificationAtom } from '../../../context';
import { TablePopUp } from '../../../components';
import * as Yup from 'yup';
import { format } from "date-fns";
import { DateFilter } from '../../../components';
import { Loader } from "../../../customs";
import { TbReload as ReloadIcon } from "react-icons/tb";
import ReactPaginate from 'react-paginate';
import { IoReloadCircleOutline as ResetIcon } from "react-icons/io5";
import React from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import apiCallFormData from "../../../utils/apiFormDataCall";
import { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiBook, FiCreditCard, FiEdit3, FiEye } from 'react-icons/fi';
import { IoMdCheckmarkCircle, IoMdCloseCircle } from 'react-icons/io';
import { MdLocationCity, MdFlag, MdHome, MdWork } from 'react-icons/md';

const HeadStyle = {
    borderBottom: "1px solid var(--dark-green-color)",
    padding: '15px 5px',
};

const DataStyle = {
    padding: '5px 5px',
};

const ROWSTYLE = styled.tr`
    & > td:last-of-type > * {
        display: none;
    }
    :hover > td {
        background-color: var(--table-highlight);
        &:last-of-type > * {
            display: block;
        }
    }
    &.active > td:last-of-type > * {
        display: block !important;
    }
`;

export const StudentBiodataAtom = atom<number | null>(null);

export default function StudentBioDataUpdate() {
  const [, set_modal] = useAtom(modalAtom);
  const [, set_open] = useAtom(openAtom);
  const [active] = useAtom(StudentBiodataAtom);
  const queryClient = useQueryClient();

  // UI / filter state
  const [searchInput, setSearchInput] = useState<string>(""); // controlled input
  const [debouncedSearch, setDebouncedSearch] = useState<string>(""); // value sent to server
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);

  // Pagination (server-driven)
  const [currentPage, setCurrentPage] = useState<number>(0); // 0-based for UI components
  const itemsPerPage = 100;

  // debounce ref
  const searchDebounceRef = useRef<number | null>(null);

  // load levels once
  const { data: allLevels = [] } = useQuery(
    ["exam-level"],
    async () => {
      const res = await apiCall().get(`/SetUp/GetAllExemption`);
      // return the expected array safely
      return Array.isArray(res.data?.Data) ? res.data.Data : Array.isArray(res.data) ? res.data : [];
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // When searchInput or selectedLevel changes, debounce and set debouncedSearch
  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    // short debounce to avoid spamming server for each keystroke
    searchDebounceRef.current = window.setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setCurrentPage(0); // reset to first page whenever search term changes
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchInput, selectedLevel]);

  // Server-side paginated query key includes debouncedSearch (StudentNo) and selectedLevel
  const queryKey = ["student-admin", currentPage, itemsPerPage, selectedLevel, debouncedSearch];

  const {
    data: serverPayload = {},
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    queryKey,
    async () => {
      const params: any = {
        PageNumber: currentPage + 1, // backend expects 1-based
        PageSize: itemsPerPage,
      };
      if (selectedLevel) params.LevelId = selectedLevel;
      if (debouncedSearch) params.StudentNo = debouncedSearch;
      const res = await apiCall().get("/exam/getAllStudentsForAdmin", { params });
      return res?.data ?? {};
    },
    {
      keepPreviousData: true,
      staleTime: 30_000,
      retry: 1,
      onError: (err) => console.error("Fetch students error:", err),
    }
  );

  // extract items + totals from server payload safely
  const serverItems: any[] = serverPayload?.reportItems ?? serverPayload?.Data ?? [];
  const totalCount: number = serverPayload?.TotalCount ?? serverPayload?.TotalSize ?? 0;
  const pageCountFromServer: number = serverPayload?.PageCount ?? Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const pageCount = Math.max(1, pageCountFromServer);

  // keep currentPage in valid range when pageCount shrinks
  useEffect(() => {
    if (currentPage >= pageCount) {
      setCurrentPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  // Handlers
  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleLevelChange = (levelId: number | null) => {
    setSelectedLevel(levelId);
    setCurrentPage(0);
    // Invalidate caches that depend on level if you want fresh data immediately:
    queryClient.invalidateQueries(["student-admin"]);
  };

  const handleReload = async () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedLevel(null);
    setCurrentPage(0);
    try {
      await refetch();
    } catch (err) {
      // ignore
    }
  };

  // pagination navigation (server pages)
  const goToPage = (pageIndex: number) => {
    if (pageIndex < 0 || pageIndex >= pageCount) return;
    setCurrentPage(pageIndex);
  };

  const nextPage = () => {
    if (currentPage + 1 < pageCount) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const firstPage = () => setCurrentPage(0);
  const lastPage = () => setCurrentPage(pageCount - 1);

  // page number render helpers (keeps compact display)
  const pagesPerGroup = 12;
  const currentGroup = Math.floor(currentPage / pagesPerGroup);
  const groupStart = currentGroup * pagesPerGroup;
  const groupEnd = Math.min(groupStart + pagesPerGroup, pageCount);

  const renderPageNumber = (pageIndex: number) => {
    const isActive = pageIndex === currentPage;
    return (
      <Box
        key={`p-${pageIndex}`}
        onClick={() => goToPage(pageIndex)}
        cursor="pointer"
        px="8px"
        py="4px"
        border="1px solid var(--muted-border-color)"
        borderRadius="4px"
        bg={isActive ? "var(--dark-green-color)" : "white"}
        color={isActive ? "white" : "black"}
        _hover={{ opacity: 0.85 }}
        marginRight="4px"
      >
        {pageIndex + 1}
      </Box>
    );
  };

  // UI indices for "Showing X - Y of Z"
  const startIndex = totalCount === 0 ? 0 : currentPage * itemsPerPage + 1;
  const endIndex = Math.min((currentPage + 1) * itemsPerPage, totalCount);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Box>
          <TopDisplay label="Student Bio Data Update Page" button_array={[]} />

          <Flex gap="20px" wrap="wrap" mt="20px">
            <InputGroup width="300px" bg="var(--white-color)" borderColor="var(--dark-green-color)">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                value={searchInput}
                onChange={onSearchInputChange}
                type="text"
                placeholder="Search by Student No (real-time)"
              />
              <span
                style={{
                  position: "absolute",
                  zIndex: "1",
                  right: "10px",
                  cursor: "pointer",
                  top: "12px",
                }}
                onClick={handleReload}
                title="Reset Filters"
              >
                <ReloadIcon />
              </span>
            </InputGroup>

            <Select
              placeholder="Select Level"
              width="250px"
              bg="var(--white-color)"
              borderColor="var(--dark-green-color)"
              value={selectedLevel ?? ""}
              onChange={(e) => handleLevelChange(Number(e.target.value) || null)}
            >
              <option value="">All Levels</option>
              {Array.isArray(allLevels) &&
                allLevels.map((lvl: any) => (
                  <option key={lvl.Id} value={lvl.Id}>
                    {lvl.Name}
                  </option>
                ))}
            </Select>
          </Flex>

          <Flex mt="25px" flexDir="column" gap="10px">
            <Text m="0" fontSize=".8rem">
              Showing {startIndex} - {endIndex} of {totalCount} {isFetching ? " (loading...)" : ""}
            </Text>
          </Flex>

          {serverItems && serverItems.length > 0 ? (
            <TableContainer mt="20px">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th sx={HeadStyle}>StudentNo</Th>
                    <Th sx={HeadStyle}>First Name</Th>
                    <Th sx={HeadStyle}>Middle Name</Th>
                    <Th sx={HeadStyle}>Last Name</Th>
                    <Th sx={HeadStyle}>Level</Th>
                    <Th sx={HeadStyle}>Email</Th>
                    <Th sx={HeadStyle}>Phone</Th>
                    <Th sx={HeadStyle} w="50px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {serverItems.map((student: any) => (
                    <ROWSTYLE key={student.Id ?? student.StudentNo} className={student.Id === active ? "active" : ""}>
                      <Td sx={DataStyle}>{student.StudentNo}</Td>
                      <Td sx={DataStyle}>{student.FirstName}</Td>
                      <Td sx={DataStyle}>{student.MiddleName}</Td>
                      <Td sx={DataStyle}>{student.LastName}</Td>
                      <Td sx={DataStyle}>{student.LevelName}</Td>
                      <Td sx={DataStyle}>{student.Email}</Td>
                      <Td sx={DataStyle}>{student.PhoneNumber}</Td>
                      <Td sx={DataStyle}>
                        <TempPopUp index={student.Id} data={student} />
                      </Td>
                    </ROWSTYLE>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Text mt="20px">No Student Data Found</Text>
          )}

          <StylePaginateBox>
            <Flex gap="8px" mt="24px" justifyContent="center" alignItems="center" flexWrap="wrap">
              <Button size="xs" onClick={firstPage} isDisabled={currentPage === 0}>
                First
              </Button>
              <Button size="xs" onClick={prevPage} isDisabled={currentPage === 0}>
                Prev
              </Button>

              {groupStart > 0 && (
                <Box px="8px" py="3px">
                  ...
                </Box>
              )}

              {Array.from({ length: groupEnd - groupStart }).map((_, idx) => renderPageNumber(groupStart + idx))}

              {groupEnd < pageCount && (
                <Box px="8px" py="3px">
                  ...
                </Box>
              )}

              <Button size="xs" onClick={nextPage} isDisabled={currentPage + 1 >= pageCount}>
                Next
              </Button>
              <Button size="xs" onClick={lastPage} isDisabled={currentPage + 1 >= pageCount}>
                Last
              </Button>
            </Flex>
          </StylePaginateBox>
        </Box>
      )}
    </>
  );
}






const StylePaginateBox = styled.div`
  width: 100%;
  & > ul {
    list-style-type: none;
    text-align: center;
    padding: 0;
    margin-top: 10px;
  }
  & > ul > li {
    display: inline;
    margin: 0 5px;
    padding: 2px 5px;
    border-radius: 3px;
    background-color: transparent;
    color: var(--dark-green-color);
    cursor: pointer;
  }
  & > ul > li.selected {
    background-color: var(--highlight-paginate-background);
    color: var(--highlight-paginate-color);
  }
  & > ul > li:first-of-type,
  & > ul > li:last-of-type {
    font-weight: bold;
  }
`;

// --- INTERFACES ---
interface Student {
  ApplicantId?: string;
  StudentNo?: string;
  FirstName?: string;
  LastName?: string;
  OtherName?: string;
  MiddleName?: string; // Add this
  Gender?: string;
  PhoneNumber?: string;
  CountryName?: string;
  StateOfOrigin?: string;
  LGA?: string;
  DateOfBirth?: string;
  Email?: string;
  ContactAddress?: string;
  ResidentialAddress?: string;
  ImageUrl?: string;
  UploadPictureUrl?: string;
  TitleId?: string;
  MaritalStatusId?: string;
  OtherTitle?: string;
  MembershipNo?: string; // This exists in your API response
  Occupation?: string;
  CorporateName?: string;
  SelectedCategory?: string;
  RouteId?: number;
  RouteName?: string;
  isActive?: boolean;
  CreateOn?: string;
  IsSubmitted?: boolean;
  HasPaidApplicationFee?: boolean;
  HasPaidAcceptanceFee?: boolean;
  HasPaidTrainingFee?: boolean;
  HasPaidInductionFee?: boolean;
  PaymentStatus?: string;
}

interface ErrorResponse {
  message?: string;
  Message?: string;
  errors?: any;
}
interface StudentFormData {
  ApplicantId: string;
  StudentNo: string;
  FirstName: string;
  MembershipNo: string;
  LastName: string;
  OtherName: string;
  DateOfBirth: string;
  Gender: string;
  Email: string;
  PhoneNumber: string;
  Nationality: string;
  StateOfOrigin: string;
  Address: string;
  Lga: string;
  Religion: string;
  MaritalStatus: string;
  WhatsAppNumber: string;
}


interface StudentBioData {
  Id: number;
  ApplicantId: string;
  TitleId: string;
  OtherTitle?: string;
  FirstName: string;
  MiddleName: string;
  LastName: string;
  MembershipNo: string;
  Email: string;
  PhoneNumber: string;
  WhatsAppNumber?: string;
  DateBirth: string;
  ContactAddress: string;
  ResidentialAddress: string;
  StateOriginId: number;
  LGAId: number;
  LGAName: string;
  StateId: number;
  StateName: string;
  CountryId: number;
  CountryName: string;
  ZoneId: number;
  Gender: string;
  MaritalStatusId: string;
  IsChangeofName: boolean;
  UploadPictureUrl?: string;
  HasDisability: boolean;
  Disability?: string;
  RouteId: number;
  HasPaidApplicationFee: boolean;
  HasPaidAcceptanceFee: boolean;
  ExemptionId?: number;
  TotalScore: number;
  
}

interface AcademicRecord {
  Id: number;
  ApplicantId: string;
  QualificationId: number;
  InstitutionName: string;
  Discipline: string;
  Qualification: string;
  StartDate: string;
  GraduateDate: string;
  CertificateUrl?: string;
  Score: number;
  LevelBy: number;
}

interface JobHistory {
  Id: number;
  ApplicationId: string;
  Organization: string;
  StartDate: string;
  EndDate?: string;
  IsCurrentJob: boolean;
  PositionHeld: string;
  CompanyAddress: string;
  Sector?: string;
  Industry?: string;
  GradeLevel?: string;
  PresentDuties?: string;
}

interface CountryData {
  [x: string]: any;
  CountryId: number;
  CountryName: string;
}

interface StateData {
  [x: string]: any;
  StateId: number;
  StateName: string;
  CountryId: number;
  CountryName: string;
  StateOriginId: number;
}

interface LGAData {
  LGAId: number;
  LGAName: string;
  StateId: number;
}

interface CompleteStudentData extends Student {
  LGAId: any;
  bioData?: StudentBioData;
  academicRecords?: AcademicRecord[];
  jobHistory?: JobHistory[];
  stateOriginName?: string;
  StateOriginId?:any;
  LGAName?: string;
  StateName?: string;
  CountryId?: any;
  CountryName?: string;
  WhatsAppNumber?: string;
  HasDisablity?: boolean;
  Disability?: string;
}

interface StudentDetailsViewProps {
  applicantId: string;
  onEdit?: () => void;
  onClose?: () => void;
}

// --- POPUP ---
const TempPopUp = (props: any) => {
  const { index, data } = props;
  const [active, set_active] = useAtom(StudentBiodataAtom);
  const [, set_open] = useAtom(openAtom);
  const [, set_modal] = useAtom(modalAtom);

  const handleClick = () => {
    set_active(index === active ? null : index);
  };

  const handleEdit = () => {
    set_modal({
      header: "Edit Student BioData",
      content: <EditBioForm student={data} />,
    });
    set_open(true);
    handleClick();
  };

  const handleView = () => {
    set_modal({
      header: "Student Profile Details",
      content: (
        <StudentDetailsView 
          applicantId={data.ApplicantId} 
          onEdit={() => {
            set_modal({
              header: "Edit Student BioData", 
              content: <EditBioForm student={data} />
            });
          }}
          onClose={() => set_open(false)}
        />
      ),
    });
    set_open(true);
    handleClick();
  };

  return (
    <TablePopUp
      click={handleClick}
      options={[
        { label: "View Details", click: handleView },
        { label: "Edit BioData", click: handleEdit }
      ]}
    />
  );
};

// Custom hook to fetch complete student data
const useCompleteStudentData = (applicantId: string) => {
  const { data: basicData, isLoading: basicLoading } = useQuery<Student>(
    ["applicant", applicantId],
    
    async () => {
    const encodedApplicantId = applicantId ? encodeURIComponent(applicantId) : undefined;

      const response = await apiCall().get(`/Applicant/GetApplicantSignup/${encodedApplicantId}`);
      return response.data?.Data || response.data;
    },
    { enabled: !!applicantId }
  );

  const { data: bioData, isLoading: bioLoading } = useQuery<StudentBioData>(
    ["biodata", applicantId],
    async () => {
    const encodedApplicantId = applicantId ? encodeURIComponent(applicantId) : undefined;

      const response = await apiCall().get(`/Applicant/GetBioDataByApplicantId?applicantId=${encodedApplicantId}`);
      return response.data?.Data;
    },
    { enabled: !!applicantId }
  );

  const { data: academicData, isLoading: academicLoading } = useQuery<AcademicRecord[]>(
    ["academic", applicantId],
    async () => {
    const encodedApplicantId = applicantId ? encodeURIComponent(applicantId) : undefined;

      const response = await apiCall().get(`/Applicant/GetAcademicByApplicantId?applicantId=${encodedApplicantId}`);
      return response.data?.Data || [];
    },
    { enabled: !!applicantId }
  );

  const { data: jobData, isLoading: jobLoading } = useQuery<JobHistory[]>(
    ["jobhistory", applicantId],
    async () => {
    const encodedApplicantId = applicantId ? encodeURIComponent(applicantId) : undefined;

      const response = await apiCall().get(`/Applicant/GetJobHistoryById?applicantId=${encodedApplicantId}`);
      return response.data?.Data || [];
    },
    { enabled: !!applicantId }
  );

        const { data: countryData } = useQuery<CountryData[]>(
        ["country"],
        async () => {
          const response = await apiCall().get("/Country/GetCountrys");
          return response.data;
        },
        { staleTime: 1000 * 60 * 30 }
      );

        const { data: statesData } = useQuery<StateData[]>(
        ["states"],
        async () => {
          const response = await apiCall().get("/State/GetAllStates");
          return response.data;
        },
        { staleTime: 1000 * 60 * 30 }
      );

      const { data: lgasData } = useQuery<LGAData[]>(
        ["lgas"],
        async () => {
          const response = await apiCall().get("/LGA/GetAllLGAs");
          return response.data;
        },
        { staleTime: 1000 * 60 * 30 }
      );

  const completeData: CompleteStudentData | undefined = React.useMemo(() => {
    if (!basicData && !bioData) return undefined;

    const stateOrigin = statesData?.find(s => s.StateId === bioData?.StateOriginId);
    const currentState = statesData?.find(s => s.StateId === bioData?.StateId);
    const country = countryData?.find(c => c.CountryId === bioData?.CountryId);
    const lga = lgasData?.find(l => l.LGAId === bioData?.LGAId);
    

   return {
  ...basicData,
  ApplicantId: bioData?.ApplicantId || basicData?.ApplicantId,
  FirstName: bioData?.FirstName || basicData?.FirstName,
  LastName: bioData?.LastName || basicData?.LastName,
  MiddleName: bioData?.MiddleName || basicData?.MiddleName,
  Email: bioData?.Email || basicData?.Email,
  PhoneNumber: bioData?.PhoneNumber || basicData?.PhoneNumber,
  Gender: bioData?.Gender || basicData?.Gender,
  DateOfBirth: bioData?.DateBirth || basicData?.DateOfBirth,
  UploadPictureUrl: bioData?.UploadPictureUrl || basicData?.UploadPictureUrl,
  ContactAddress: bioData?.ContactAddress || basicData?.ContactAddress,
  ResidentialAddress: bioData?.ResidentialAddress || basicData?.ResidentialAddress,

  // ✅ normalized naming
  StateOriginId: bioData?.StateOriginId,
  LGAId: bioData?.LGAId,
  StateId: bioData?.StateId,
  CountryId: bioData?.CountryId,

  StateName: currentState?.StateName?.trim() || bioData?.StateName,
  StateOriginName: stateOrigin?.StateName?.trim() || bioData?.StateOriginId,
  LGAName: lga?.LGAName?.trim() || bioData?.LGAName,
  CountryName: country?.CountryName?.trim() || bioData?.CountryName || currentState?.CountryName, // ✅ proper fallback

  TitleId: bioData?.TitleId,
  OtherTitle: bioData?.OtherTitle,
  MaritalStatusId: bioData?.MaritalStatusId,
  WhatsAppNumber: bioData?.WhatsAppNumber,
  HasDisability: bioData?.HasDisability,
  Disability: bioData?.Disability,
  IsChangeofName: bioData?.IsChangeofName,

  bioData,
  academicRecords: academicData,
  jobHistory: jobData,
};

  }, [basicData, bioData, academicData, jobData, countryData, statesData, lgasData]);

  const isLoading = basicLoading || bioLoading || academicLoading || jobLoading;

  return {
    data: completeData,
    isLoading,
    basicData,
    bioData,
    academicData,
    jobData,
    countryData,
    statesData,
    lgasData
  };
};

// --- FORM ---

const EditBioForm = ({ student }: { student: Student }) => {
  const queryClient = useQueryClient();
  const { data: completeStudent, statesData, countryData, lgasData, isLoading } = useCompleteStudentData(student?.ApplicantId || "");

// 🔹 remove Nationality from formData state
const [formData, setFormData] = useState({
  ApplicantId: student?.ApplicantId || "",
  StudentNo: student?.StudentNo || student?.MembershipNo || "",
  MembershipNo: "",
  FirstName: "",
  LastName: "",
  OtherName: "",
  DateOfBirth: "",
  Gender: "",
  Email: "",
  PhoneNumber: "",
  CountryId: "",       // ✅ add this
  CountryName: "",
  StateOriginName: "",   // keep as StateId
  StateOriginId: "",
  LGAId: "",           // ✅ store Id not Name
  ContactAddress: "",
  ResidentialAddress: "",
  TitleId: "",
  MaritalStatusId: "",
  OtherTitle: "",
  WhatsAppNumber: "",
});


// 🔹 find the state in statesData
const selectedState = statesData?.find(
  (s: StateData) => String(s.StateId) === String(formData.StateOriginId)
);

// 🔹 derive CountryId
const selectedCountry = statesData?.find(
  (c: CountryData) => String(c.CountryId) === String(formData.CountryId)
);


// ✅ Sync when completeStudent updates
useEffect(() => {
  if (completeStudent && statesData?.length) {
    const derivedState = statesData.find(
      (s: StateData) =>
        String(s.StateId) === String(completeStudent.StateOfOrigin)
    );

    const derivedCountryId = derivedState?.CountryId || "";

    setFormData(prev => ({
      ...prev,
      ApplicantId: completeStudent.ApplicantId || student?.ApplicantId || "",
      StudentNo:
        completeStudent.StudentNo ||
        completeStudent.MembershipNo ||
        student?.StudentNo ||
        student?.MembershipNo ||
        "",
      FirstName: completeStudent.FirstName || "",
      LastName: completeStudent.LastName || "",
      OtherName: completeStudent.OtherName || completeStudent.MiddleName || "",
      DateOfBirth: completeStudent.DateOfBirth?.split("T")[0] || "",
      Gender: completeStudent.Gender || "",
      Email: completeStudent.Email || "",
      PhoneNumber: completeStudent.PhoneNumber || "",
      CountryId: completeStudent.CountryId || prev.CountryId,
      StateOriginId: completeStudent.StateOriginId || prev.StateOriginId,
      LGAId: completeStudent.LGAId || prev.LGAId,
      ContactAddress: completeStudent.ContactAddress || "",
      ResidentialAddress: completeStudent.ResidentialAddress || "",
      TitleId: completeStudent.TitleId || "",
      MaritalStatusId: completeStudent.MaritalStatusId || "",
      OtherTitle: completeStudent.OtherTitle || "",
      WhatsAppNumber: completeStudent.WhatsAppNumber || "",
    }));
  }
}, [completeStudent, statesData]);




  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const defaultImage = "/public-assets/profile-not-found.png";
  const imageUrl = completeStudent?.UploadPictureUrl || student?.ImageUrl || defaultImage;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
) => {
  const { name, value } = e.target;

  setFormData((prev) => {
    let updated = { ...prev, [name]: value };

    if (name === "CountryId") {
      updated.StateOriginId = "";
      updated.LGAId = "";
    } else if (name === "StateOfOrigin") {
      updated.LGAId = "";
    }

    return updated;
  });
};

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024;
      if (!validTypes.includes(file.type)) {
        setErrors(["Please upload a valid image (JPEG, PNG, or GIF)."]);
        return;
      }
      if (file.size > maxSize) {
        setErrors(["Image size must be less than 5MB."]);
        return;
      }
      setProfilePic(file);
      setPreviewUrl(URL.createObjectURL(file));
      setErrors([]);
    } else {
      setProfilePic(null);
      setPreviewUrl(null);
      setErrors(["No file selected."]);
    }
  };

  const updatePicMutation = useMutation<
    AxiosResponse<any>,
    AxiosError<ErrorResponse>,
    void
  >({
    mutationFn: async () => {
      if (!profilePic) throw new Error("No profile picture selected.");
      if (!formData.StudentNo)
        throw new Error("StudentNo is required for picture update.");

      const form = new FormData();
      form.append("Picture", profilePic);
      form.append("StudentNo", formData.StudentNo);

      return apiCallFormData().post("/Exam/updateProfilePictureAdmin", form);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(["exam-bio"]);
      queryClient.invalidateQueries(["applicantId", formData.ApplicantId]);
      setProfilePic(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setErrors([]);
      if (response.data?.newImageUrl) {
        setFormData((prev) => ({
          ...prev,
          ImageUrl: response.data.newImageUrl,
        }));
      }
      alert("Profile picture updated!");
    },
    onError: (error) => {
      console.error("Picture update error:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        error.response?.data?.errors?.[0] ||
        error.message ||
        "Failed to update profile picture.";
      setErrors([errorMsg]);
    },
  });
const updateBioMutation = useMutation<
  AxiosResponse<any>,
  AxiosError<ErrorResponse>,
  void
>({
  mutationFn: async () => {
    if (!statesData) throw new Error("States data not loaded");

    // Base apiData object
    const apiData: Record<string, string> = {
      TitleId: formData.TitleId || "",
      MaritalStatusId: formData.MaritalStatusId || "",
      OtherTitle: formData.OtherTitle || "",
      StudentNo: formData.StudentNo || "",
      FirstName: formData.FirstName || "",
      LastName: formData.LastName || "",
      MiddleName: formData.OtherName || "",
      Email: formData.Email || "",
      PhoneNumber: formData.PhoneNumber || "",
      DateBirth: formData.DateOfBirth || "",
      ContactAddress: formData.ContactAddress || "",
      ResidentialAddress: formData.ResidentialAddress || "",
      GenderId: formData.Gender || "",
    };

    // ============================
    // ✅ FIXED LOCATION LOGIC
    // ============================

    // If State selected
    if (formData.StateOriginId) {
      const derivedState = statesData.find(
        (s: StateData) =>
          String(s.StateId) === String(formData.StateOriginId)
      );

      if (derivedState) {
        apiData.StateOriginId = String(derivedState.StateId);

        // derive country from state
        if (derivedState.CountryId) {
          apiData.CountryId = String(derivedState.CountryId);
        }

        // LGA optional
        if (formData.LGAId) {
          apiData.LGAId = String(formData.LGAId);
        }
      }
    }

    // If no State but LGA selected
    else if (formData.LGAId) {
      const derivedLga = lgasData?.find(
        (l: LGAData) =>
          String(l.LGAId) === String(formData.LGAId)
      );

      if (derivedLga) {
        apiData.LGAId = String(derivedLga.LGAId);
        apiData.StateOriginId = String(derivedLga.StateId);

        const derivedState = statesData.find(
          (s: StateData) =>
            String(s.StateId) === String(derivedLga.StateId)
        );

        if (derivedState?.CountryId) {
          apiData.CountryId = String(derivedState.CountryId);
        }
      }
    }

    // If only Country selected
    else if (formData.CountryId) {
      apiData.CountryId = String(formData.CountryId);
    }

    // ============================
    // Build FormData
    // ============================

    const fd = new FormData();

    Object.entries(apiData).forEach(([key, value]) => {
      if (value !== "") {
        fd.append(key, value);
      }
    });

    return apiCallFormData().post(
      "/Exam/updateStudentDataAdmin",
      fd
    );
  },

  onSuccess: () => {
    queryClient.invalidateQueries(["exam-bio"]);
    queryClient.invalidateQueries(["applicant", formData.ApplicantId]);
    setErrors([]);
    alert("Biodata updated successfully!");
  },

  onError: (error) => {
    console.error("Bio update error:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.response?.data?.Message ||
      error.response?.data?.errors?.[0] ||
      error.message ||
      "Failed to update biodata. Please try again.";

    setErrors([errorMsg]);
  },
});


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    updateBioMutation.mutate();
  };
  
type StudentFormField = keyof StudentFormData;

// useEffect(() => {
//   if (formData.StateOfOrigin) {
//     loadLGAOptions(formData.StateOfOrigin);
//   }
// }, [formData.StateOfOrigin]);

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        maxWidth: "650px",
        margin: "2rem auto",
        background: "#fff",
        padding: "2rem",
        borderRadius: "12px",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
        display: "grid",
        gap: "1.2rem",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {errors.length > 0 && (
        <div style={{ color: "red", fontWeight: "bold" }}>
          {errors.map((err, idx) => (
            <p key={idx}>{err}</p>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <div style={{ textAlign: "center" }}>
          <img
            src={previewUrl || imageUrl}
            width="100"
            height="100"
            style={{ borderRadius: "10%", objectFit: "cover" }}
            alt="Profile"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = defaultImage;
            }}
          />
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              marginTop: "10px",
              padding: "6px 12px",
              background: "#FF9800",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.85rem",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Edit Picture
          </button>
          {profilePic && (
            <button
              type="button"
              onClick={() => updatePicMutation.mutate()}
              disabled={updatePicMutation.isLoading}
              style={{
                marginTop: "10px",
                padding: "6px 12px",
                background: "#1b431c",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.85rem",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                marginLeft: "8px",
              }}
            >
              {updatePicMutation.isLoading ? "Saving..." : "Save Picture"}
            </button>
          )}
        </div>
      </div>  

     {[
      {
        label: "Student No",
        name: "StudentNo",
        readOnly: true,
      },
        { label: "Title", name: "TitleId", type: "select",
          options: ["", "Mr", "Mrs", "Miss", "Dr", "Prof"].map(v => ({ value: v, label: v }))
        },
        { label: "Other Title", name: "OtherTitle" },
        { label: "First Name *", name: "FirstName" },
        { label: "Last Name *", name: "LastName" },
        { label: "Other Name", name: "OtherName" },
        { label: "Date of Birth *", name: "DateOfBirth", type: "date" },
        { label: "Gender", name: "Gender", type: "select",
          options: ["", "Male", "Female"].map(v => ({ value: v, label: v }))
        },
        { label: "Marital Status", name: "MaritalStatusId", type: "select",
          options: ["", "Single", "Married", "Divorced", "Widowed"].map(v => ({ value: v, label: v }))
        },
        { label: "Email", name: "Email", type: "email" },
        { label: "Phone Number", name: "PhoneNumber" },

     {
        label: "Nationality",
        name: "CountryId",
        type: "select",
        options:
          countryData?.map((c: CountryData) => ({
            value: String(c.CountryId),
            label: c.CountryName,
          })) || [],
      }
,
          {
        label: "State of Origin",
        name: "StateOriginId",
        type: "select",
        options:
          statesData
            ?.filter((s: StateData) =>
              !formData.CountryId || // 👈 KEY FIX
              String(s.CountryId) === String(formData.CountryId)
            )
            .map((s: StateData) => ({
              value: String(s.StateId),
              label: s.StateName,
            })) || [],
      }
       ,
            {
          label: "LGA",
          name: "LGAId",
          type: "select",
          options:
            lgasData
              ?.filter((l: LGAData) =>
                !formData.StateOriginId || // 👈 KEY FIX
                String(l.StateId) === String(formData.StateOriginId)
              )
              .map((l: LGAData) => ({
                value: String(l.LGAId),
                label: l.LGAName,
              })) || [],
        }
         ,
        { label: "Contact Address", name: "ContactAddress", textarea: true },
        { label: "Residential Address", name: "ResidentialAddress", textarea: true },
        ].map((field) => (
        <div key={field.name} style={{ marginBottom: "16px" }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
            {field.label}
          </label>
          {field.textarea ? (
            <textarea
              name={field.name}
              value={(formData as any)[field.name] || ""}
              onChange={handleChange}
              readOnly={field.readOnly}
              required={field.label.includes("*")}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #1b431c",
                borderRadius: "6px",
                background: field.readOnly ? "#f3f3f3" : "#fff",
              }}
            />
          ) : field.type === "select" ? (
            <select
              name={field.name}
              value={formData[field.name as keyof typeof formData] ?? ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #1b431c",
                borderRadius: "6px",
                background: "#fff",
              }}
            >
              {!formData[field.name as keyof typeof formData] && (
                <option value="">-- Select --</option>
              )}

              {/* retain hidden initial value if not inside options */}
              {formData[field.name as keyof typeof formData] &&
                !(field.options || []).some(
                  (o: any) =>
                    String(o.value) ===
                    String(formData[field.name as keyof typeof formData])
                ) && (
                  <option
                    value={formData[field.name as keyof typeof formData]}
                    hidden
                  >
                    {formData[field.name as keyof typeof formData]}
                  </option>
                )}

              {(field.options || []).map((option: any, idx: number) => (
                <option key={idx} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

          ) : (
            <input
              type={field.type || "text"}
              name={field.name}
              value={(formData as any)[field.name] || ""}
              onChange={handleChange}
              readOnly={field.readOnly}
              required={field.label.includes("*")}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #1b431c",
                borderRadius: "6px",
                background: field.readOnly ? "#f3f3f3" : "#fff",
              }}
            />
          )}

        </div>
      ))}

      <button
        type="submit"
        disabled={updateBioMutation.isLoading}
        style={{
          padding: "12px",
          background: "#4CAF50",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "1rem",
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
        }}
      >
        {updateBioMutation.isLoading ? "Updating..." : "Save Changes"}
      </button>
    </form>
  );
};
// const [formData, setFormData] = useState<any>({});


const DetailCard = styled(Card)`
  border: 1px solid var(--dark-green-color);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(27, 67, 28, 0.15);
  }
`;

const InfoRow = styled(Flex)`
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

const StudentDetailsView: React.FC<StudentDetailsViewProps> = ({ 
  applicantId, 
  onEdit,
  onClose 
}) => {
  const [viewMode, setViewMode] = useState<'details' | 'summary' | 'academic' | 'work'>('details');
  
  const { data: studentData, isLoading } = useCompleteStudentData(applicantId);
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const labelColor = useColorModeValue('gray.700', 'gray.400');

  if (isLoading) {
    return <Loader />;
  }

  if (!studentData) {
    return (
      <Container maxW="4xl" py={8}>
        <Box 
          textAlign="center" 
          py={10} 
          px={6}
          bg={cardBg}
          borderRadius="lg"
          border="1px solid var(--dark-green-color)"
        >
          <Text color="red.500" fontSize="lg">
            Error loading student details. Please try again.
          </Text>
        </Box>
      </Container>
    );
  }

const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return "Not provided";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (trimmed === "") {
      return "Not provided";
    }

    // Check if the string looks like a date
    const date = new Date(trimmed);
    if (!isNaN(date.getTime()) && trimmed.match(/^\d{4}-\d{2}-\d{2}/)) {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    }

    return trimmed; // ✅ show countryName, stateName, LGAName, etc.
  }

  return String(value);
};



  const InfoField = ({ 
    icon, 
    label, 
    value, 
    colorValue 
  }: { 
    icon: any; 
    label: string; 
    value: any; 
    colorValue?: string 
  }) => (
    <InfoRow align="center" justify="space-between">
      <HStack spacing={3} flex={1}>
        <Icon as={icon} color="var(--dark-green-color)" />
        <Text fontWeight="600" color={labelColor} fontSize="sm">
          {label}:
        </Text>
      </HStack>
      <Text 
        color={colorValue || textColor} 
        fontSize="sm" 
        textAlign="right"
        maxW="60%"
        wordBreak="break-word"
      >
        {formatValue(value)}
      </Text>
    </InfoRow>
  );

  const StatusBadge = ({ status, label }: { status: boolean | undefined; label: string }) => (
    <Flex align="center" justify="space-between" py={2}>
      <Text fontSize="sm" color={labelColor}>{label}:</Text>
      <Badge 
        colorScheme={status ? "green" : "red"} 
        variant="subtle"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Icon as={status ? IoMdCheckmarkCircle : IoMdCloseCircle} />
        {status ? "Paid" : "Pending"}
      </Badge>
    </Flex>
  );
  

const updatedPersonalFields = [
  { icon: FiUser  , label: "Student No", value: studentData.StudentNo || studentData.MembershipNo || studentData.ApplicantId },
  { icon: FiUser  , label: "Title", value: studentData.TitleId },
  { icon: FiUser  , label: "Other Title", value: studentData.OtherTitle },
  { icon: FiUser  , label: "Full Name", value: `${studentData.FirstName} ${studentData.MiddleName || ''} ${studentData.LastName}`.trim() },
  { icon: FiUser  , label: "Gender", value: studentData.Gender },
  { icon: FiUser  , label: "Marital Status", value: studentData.MaritalStatusId },
  { icon: FiCalendar, label: "Date of Birth", value: formatValue(studentData.DateOfBirth || studentData.bioData?.DateBirth) },
  { icon: MdWork, label: "Occupation", value: studentData.Occupation },
  { icon: MdFlag, label: "Nationality", value: formatValue(studentData.CountryName) },
  { icon: FiUser  , label: "Has Disability", value: studentData.HasDisablity ? "Yes" : "No" },
  { icon: FiUser  , label: "Disability Type", value: studentData.Disability },
];
const updatedContactFields = [
  { icon: FiMail, label: "Email Address", value: studentData.Email },
  { icon: FiPhone, label: "Phone Number", value: studentData.PhoneNumber },
  { icon: FiPhone, label: "WhatsApp Number", value: studentData.WhatsAppNumber },
  { icon: MdLocationCity, label: "State of Origin", value: formatValue( studentData.StateOriginId) },
{ icon: MdLocationCity, label: "LGA of Origin", value: formatValue(studentData.LGAName) },
  { icon: MdLocationCity, label: "Current State", value: formatValue(studentData.StateName) },
  { icon: MdHome, label: "Contact Address", value: formatValue(studentData.ContactAddress) },
  { icon: MdHome, label: "Residential Address", value: formatValue(studentData.ResidentialAddress) },
];

  const academicSection = (
    <DetailCard>
      <CardHeader pb={2}>
        <Heading size="md" color="var(--dark-green-color)">
          Academic Background
        </Heading>
      </CardHeader>
      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {studentData.academicRecords?.map((record, index) => (
            <DetailCard key={record.Id || index}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Heading size="sm" color="var(--dark-green-color)">
                    {record.InstitutionName}
                  </Heading>
                  <Text><strong>Qualification:</strong> {record.Qualification}</Text>
                  <Text><strong>Discipline:</strong> {record.Discipline}</Text>
                  <Text><strong>Duration:</strong> {new Date(record.StartDate).getFullYear()} - {new Date(record.GraduateDate).getFullYear()}</Text>
                  {record.CertificateUrl && (
                    <Button size="xs" as="a" href={record.CertificateUrl} target="_blank">
                      View Certificate
                    </Button>
                  )}
                </VStack>
              </CardBody>
            </DetailCard>
          )) || <Text>No academic records found</Text>}
        </VStack>
      </CardBody>
    </DetailCard>
  );

  const workSection = (
    <DetailCard>
      <CardHeader pb={2}>
        <Heading size="md" color="var(--dark-green-color)">
          Work Experience
        </Heading>
      </CardHeader>
      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {studentData.jobHistory?.map((job, index) => (
            <DetailCard key={job.Id || index}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Flex justify="space-between" w="full">
                    <Heading size="sm" color="var(--dark-green-color)">
                      {job.Organization}
                    </Heading>
                    {job.IsCurrentJob && (
                      <Badge colorScheme="green" size="sm">Current</Badge>
                    )}
                  </Flex>
                  <Text><strong>Position:</strong> {job.PositionHeld}</Text>
                  <Text><strong>Duration:</strong> {new Date(job.StartDate).toLocaleDateString()} - {job.EndDate ? new Date(job.EndDate).toLocaleDateString() : 'Present'}</Text>
                  <Text><strong>Address:</strong> {job.CompanyAddress}</Text>
                  {job.Sector && <Text><strong>Sector:</strong> {job.Sector}</Text>}
                  {job.Industry && <Text><strong>Industry:</strong> {job.Industry}</Text>}
                </VStack>
              </CardBody>
            </DetailCard>
          )) || <Text>No work experience found</Text>}
        </VStack>
      </CardBody>
    </DetailCard>
  );

  return (
    <Container maxW="6xl" py={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="var(--dark-green-color)">
          Student Profile Details
        </Heading>
        <HStack spacing={3}>
          <Button
            size="sm"
            variant={viewMode === 'summary' ? 'solid' : 'outline'}
            colorScheme="gray"
            leftIcon={<FiEye />}
            onClick={() => setViewMode('summary')}
          >
            Summary
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'details' ? 'solid' : 'outline'}
            colorScheme="gray"
            leftIcon={<FiUser  />}
            onClick={() => setViewMode('details')}
          >
            Details
          </Button>
          {onEdit && (
            <Button
              size="sm"
              bg="var(--dark-green-color)"
              color="white"
              leftIcon={<FiEdit3 />}
              onClick={onEdit}
              _hover={{ bg: "green.600" }}
            >
              Edit
            </Button>
          )}
          {onClose && (
            <Button size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </HStack>
      </Flex>
      {viewMode === 'summary' ? (
        <DetailCard>
          <CardBody>
            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
              <Box textAlign="center">
                <Image
                  src={studentData.UploadPictureUrl}
                  alt="Profile"
                  boxSize="150px"
                  borderRadius="full"
                  border="4px solid var(--dark-green-color)"
                  objectFit="cover"
                  fallbackSrc="https://via.placeholder.com/150"
                  mb={4}
                />
                {/* <Badge 
                  colorScheme={studentData.isActive ? "green" : "red"}
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  {studentData.isActive ? "Active" : "Inactive"}
                </Badge> */}
              </Box>
              <Box flex={1}>
                <VStack align="start" spacing={4}>
                  <Box>
                    <Heading size="md" color="var(--dark-green-color)">
                      {studentData.FirstName} {studentData.LastName}
                    </Heading>
                    <Text color={textColor} fontSize="lg">
                      {studentData.MembershipNo || studentData.StudentNo}
                    </Text>
                  </Box>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                    <InfoField icon={FiMail} label="Email" value={studentData.Email} />
                    <InfoField icon={FiPhone} label="Phone" value={studentData.PhoneNumber} />
                    <InfoField icon={MdWork} label="Occupation" value={studentData.Occupation} />
                    <InfoField icon={FiUser } label="Gender" value={studentData.Gender} />
                  </Grid>
                  <Box w="full">
                    <Text fontWeight="600" mb={2} color={labelColor}>Payment Status:</Text>
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <StatusBadge status={studentData.HasPaidApplicationFee} label="Application Fee" />
                      <StatusBadge status={studentData.HasPaidAcceptanceFee} label="Acceptance Fee" />
                      <StatusBadge status={studentData.HasPaidTrainingFee} label="Training Fee" />
                      <StatusBadge status={studentData.HasPaidInductionFee} label="Induction Fee" />
                    </Grid>
                  </Box>
                </VStack>
              </Box>
            </Flex>
          </CardBody>
        </DetailCard>
      ) : (
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
          <DetailCard>
            <CardHeader pb={2}>
              <Heading size="md" color="var(--dark-green-color)">
                <Icon as={FiUser } mr={2} />
                Personal Information
              </Heading>
            </CardHeader>
            <CardBody pt={0}>
              <Box textAlign="center" mb={4}>
                <Image
                  src={studentData.UploadPictureUrl}
                  alt="Profile"
                  boxSize="120px"
                  borderRadius="full"
                  border="3px solid var(--dark-green-color)"
                  objectFit="cover"
                  fallbackSrc="https://via.placeholder.com/120"
                  mx="auto"
                  mb={3}
                />
                {/* <Badge 
                  colorScheme={studentData.isActive ? "green" : "red"}
                  fontSize="sm"
                >
                  {studentData.isActive ? "Active Account" : "Inactive Account"}
                </Badge> */}
              </Box>
              
              <VStack spacing={0}>
                {updatedPersonalFields.map((field, index) => (
                  <InfoField key={index} icon={field.icon} label={field.label} value={field.value} />
                ))}
              </VStack>
            </CardBody>
          </DetailCard>
          <DetailCard>
            <CardHeader pb={2}>
              <Heading size="md" color="var(--dark-green-color)">
                <Icon as={FiMail} mr={2} />
                Contact Information
              </Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={0}>
                {updatedContactFields.map((field, index) => (
                  <InfoField key={index} icon={field.icon} label={field.label} value={field.value} />
                ))}
              </VStack>
            </CardBody>
          </DetailCard>
          {academicSection}
          {workSection}
          <DetailCard>
            <CardHeader pb={2}>
              <Heading size="md" color="var(--dark-green-color)">
                <Icon as={FiCreditCard} mr={2} />
                Payment Status
              </Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3} align="stretch">
                <StatusBadge status={studentData.HasPaidApplicationFee} label="Application Fee" />
                <StatusBadge status={studentData.HasPaidAcceptanceFee} label="Acceptance Fee" />
                {/* <StatusBadge status={studentData.HasPaidTrainingFee} label="Training Fee" /> */}
                <StatusBadge status={studentData.HasPaidInductionFee} label="Induction Fee" />
                <Divider />
                <Flex justify="space-between" align="center">
                  <Text fontWeight="600" color={labelColor}>Overall Status:</Text>
                  <Badge 
                    colorScheme={studentData.PaymentStatus === "Completed" ? "green" : "orange"}
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {studentData.PaymentStatus || "Pending"}
                  </Badge>
                </Flex>
                <Flex justify="space-between" align="center">
                  <Text fontWeight="600" color={labelColor}>Application Submitted:</Text>
                  <Badge 
                    colorScheme={studentData.IsSubmitted ? "green" : "red"}
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {studentData.IsSubmitted ? "Yes" : "No"}
                  </Badge>
                </Flex>
              </VStack>
            </CardBody>
          </DetailCard>
        </Grid>
      )}
    </Container>
  );
};
